import type { Edges, MapNode, NodeId } from './types';

/**
 * A forward index over the tree.
 *
 * `edges` is inverted — child -> parent — so in the vanilla app every "get the children of X"
 * is a full scan of the whole object:
 *
 *     function childrenOf(id){ const r=[]; for(const c in edges) if(edges[c]===id) r.push(c); return r; }
 *
 * That is fine once. It is not fine inside `resolveCollisions()`, which calls it from within
 * an O(n²) loop run up to 60 times — making the real cost O(passes x n² x n). Building the
 * forward map once turns every lookup into an object read.
 *
 * Memoised on the IDENTITY of the edges object, so callers can build freely in a render path
 * without thinking about it. The corollary is the one rule here: **treat `edges` as
 * immutable.** Mutating it in place leaves a stale index behind, and the vanilla app does
 * exactly that (`edges[id] = parent` on reparent), so any code bridging the two must replace
 * the object rather than edit it.
 */
export interface GraphIndex {
  /** Direct children, in the order they appear in `edges`. Never null. */
  childrenOf(id: NodeId): readonly NodeId[];
  parentOf(id: NodeId): NodeId | undefined;
  /** Every descendant, depth-first. Order matches the vanilla `descendants()`. */
  descendants(id: NodeId): NodeId[];
  /** Ancestors from the immediate parent upwards. */
  ancestors(id: NodeId): NodeId[];
  /** True when `ancestor` is `id` itself or above it. */
  isAncestorOf(ancestor: NodeId, id: NodeId): boolean;
  /** Ids with no parent entry — normally one, but the data does not guarantee it. */
  roots(): readonly NodeId[];
  hasChildren(id: NodeId): boolean;
}

/**
 * `edges` with one child re-parented — a COPY, never an edit in place.
 *
 * This is the enforcement of the rule the header states. `buildGraph` memoises on the
 * identity of the object it was given, so `S.edges[child] = parent` leaves the previous
 * index in the cache and every `childrenOf()` afterwards answers about the tree as it was.
 * That is not a theoretical hazard: it is why Ctrl-clicking twice put both new children at
 * the same coordinates. The second `addChild` asked for the parent's children, was told
 * about the tree before the first one, and placed the newcomer exactly on top of it.
 *
 * `parent === null` removes the entry, which is how a node becomes a root.
 *
 * Copying the whole object per mutation is O(n) on a 152-node map — nothing next to the
 * layout pass that follows every one of these, and it buys a container identity that means
 * "this changed", which is the only signal the memoisation can read.
 */
export function withParent(edges: Edges, child: NodeId, parent: NodeId | null): Edges {
  const next: Edges = { ...edges };
  if (parent === null) delete next[child];
  else next[child] = parent;
  return next;
}

const NONE: readonly NodeId[] = Object.freeze([]);

/** Depth beyond which a parent walk is treated as a cycle rather than a very deep tree. */
const CYCLE_GUARD = 10_000;

const cache = new WeakMap<Edges, GraphIndex>();

export function buildGraph(edges: Edges): GraphIndex {
  const hit = cache.get(edges);
  if (hit) return hit;

  const forward = new Map<NodeId, NodeId[]>();
  const known = new Set<NodeId>();
  for (const child in edges) {
    const parent = edges[child];
    known.add(child);
    known.add(parent);
    const list = forward.get(parent);
    if (list) list.push(child);
    else forward.set(parent, [child]);
  }

  const index: GraphIndex = {
    childrenOf: (id) => forward.get(id) ?? NONE,
    parentOf: (id) => edges[id],
    hasChildren: (id) => forward.has(id),

    descendants(id) {
      // Iterative and stack-based, matching the vanilla implementation exactly — including
      // its ordering, which some callers (subtree drag, delete) implicitly rely on.
      const out: NodeId[] = [];
      const stack: NodeId[] = [...(forward.get(id) ?? NONE)];
      const seen = new Set<NodeId>();
      while (stack.length) {
        const c = stack.pop()!;
        // A cycle in `edges` would otherwise hang the tab. The data should never contain
        // one — reparent refuses to drop a node onto its own descendant — but a corrupted
        // sync should degrade, not freeze.
        if (seen.has(c)) continue;
        seen.add(c);
        out.push(c);
        for (const g of forward.get(c) ?? NONE) stack.push(g);
      }
      return out;
    },

    ancestors(id) {
      const out: NodeId[] = [];
      let cur = edges[id];
      let guard = 0;
      while (cur !== undefined && guard++ < CYCLE_GUARD) {
        out.push(cur);
        cur = edges[cur];
      }
      return out;
    },

    isAncestorOf(ancestor, id) {
      if (ancestor === id) return true;
      let cur = edges[id];
      let guard = 0;
      while (cur !== undefined && guard++ < CYCLE_GUARD) {
        if (cur === ancestor) return true;
        cur = edges[cur];
      }
      return false;
    },

    roots() {
      const out: NodeId[] = [];
      for (const id of known) if (edges[id] === undefined) out.push(id);
      return out;
    },
  };

  cache.set(edges, index);
  return index;
}

/**
 * Is `id` hidden by a collapsed ancestor?
 *
 * Deliberately NOT including the permission check that the vanilla `isHidden()` folds in —
 * that belongs to `lib/permissions.ts`, and mixing the two is what made the vanilla version
 * impossible to test without a `perm` global. `isHidden()` in `lib/visibility.ts` composes
 * the two back together for callers that want the original behaviour.
 *
 * `forceOpen` holds ids temporarily treated as expanded so a search or tag hit is visible.
 * It is derived every time from the active filters and never persisted.
 */
export function isCollapsedAway(
  id: NodeId,
  opts: {
    edges: Edges;
    collapsed: (id: NodeId) => boolean;
    forceOpen?: ReadonlySet<NodeId>;
  },
): boolean {
  let cur = opts.edges[id];
  let guard = 0;
  while (cur !== undefined && guard++ < CYCLE_GUARD) {
    if (opts.collapsed(cur) && !opts.forceOpen?.has(cur)) return true;
    cur = opts.edges[cur];
  }
  return false;
}

/**
 * "Is any branch open" — the whole state of the Expand all / Collapse all toggle.
 *
 * ONE implementation, because there were two and they had to agree: `actions.ts` decided what
 * the button DOES from live state, `topbarControls.tsx` decided what it SAYS from the
 * snapshot, and a button whose label disagrees with its action is the failure mode that
 * arrangement invites.
 *
 * THE POLARITY IS DELIBERATE AND IS NOT "any collapsed" (V0.48.1, at the user's request). The
 * predecessor asked whether ANY branch was still shut, so on a map with one branch open and
 * forty shut the button offered "Expand all" — and a reader who had opened exactly the branch
 * they wanted had no one press that put the map back. Asking whether any branch is OPEN
 * reverses that: a fully collapsed tree offers Expand all, and the first branch opened —
 * however it was opened — turns the button into Collapse all.
 *
 * The root is excluded because it is never collapsible, and a childless node because
 * collapsing it means nothing.
 */
export function anyExpanded(
  nodes: Readonly<Record<NodeId, Pick<MapNode, 'root' | 'collapsed'>>>,
  edges: Edges,
): boolean {
  const g = buildGraph(edges);
  for (const id in nodes) {
    const n = nodes[id];
    if (!n.root && !n.collapsed && g.hasChildren(id)) return true;
  }
  return false;
}
